import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { searchListings, type ListingSearchResult } from "@/api/client";
import CompanyLogo from "@/features/holdings/components/CompanyLogo";
import { useCurrencies } from "@/hooks/useCurrencies";
import { useT } from "@/lib/translation";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ResearchSearchInputProps {
  /** "sm" = compact header bar, "lg" = landing page hero */
  size?: "sm" | "lg";
  placeholder?: string;
  className?: string;
  onSelect?: (ticker: string, exchange?: string | null) => void;
  /** Called with the full result when one is picked from the dropdown, before onSelect or navigation. */
  onResultSelect?: (result: ListingSearchResult) => void;
  excludeEtf?: boolean;
  /** Only show listings that have financial_reports data (e.g. for the Charts builder). */
  requireFinancialReports?: boolean;
  /** Hide the search icon (e.g. in a tight table cell). Defaults to shown. */
  showIcon?: boolean;
  /** External current value to display when the field isn't actively being edited. */
  value?: string;
  /**
   * A ticker to show as greyed placeholder text when the field is empty, and to
   * accept (calling onSelect) if the user presses Tab while it's empty — e.g. "same
   * ticker as the row above" suggestions in the Charts builder.
   */
  ghostTicker?: { ticker: string; exchange?: string | null } | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ResearchSearchInput = ({
  size = "sm",
  placeholder,
  className,
  onSelect,
  onResultSelect,
  excludeEtf = false,
  requireFinancialReports = false,
  showIcon = true,
  value,
  ghostTicker = null,
}: ResearchSearchInputProps) => {
  const navigate = useNavigate();
  const t = useT();
  const [query, setQuery] = useState(value ?? "");
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const trimmed = query.trim();
  const { currencies } = useCurrencies();
  const supportedCurrencyCodes = new Set<string>(currencies.map((c) => c.code));

  // Query the local listings DB — enabled once ≥ 2 chars
  const { data: rawResults = [], isFetching } = useQuery<ListingSearchResult[]>(
    {
      queryKey: ["researchListingSearch", trimmed, excludeEtf],
      queryFn: () => searchListings(trimmed, excludeEtf),
      enabled: trimmed.length >= 1,
      staleTime: 60_000,
    },
  );

  // Keep listings in supported currencies
  const results = rawResults.filter(
    (r) =>
      (!r.currency || supportedCurrencyCodes.has(r.currency.toUpperCase())) &&
      (!requireFinancialReports || r.has_financial_reports),
  );

  const showDropdown = open && trimmed.length >= 1;

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Reset selected index when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [trimmed]);

  // Reflect an externally-committed value (e.g. the row's chosen ticker) into the
  // input's text, but only while the user isn't actively editing/searching — never
  // clobber in-progress typing.
  useEffect(() => {
    if (!open && value !== undefined) {
      setQuery(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleSelect = (ticker: string, exchange?: string | null) => {
    // In "external value" mode (Charts builder), the field always shows the current
    // ticker — including when the user reselects the SAME one from the dropdown, which
    // doesn't change the `value` prop and so wouldn't otherwise re-trigger the sync
    // effect below, leaving the field stuck blank. Uncontrolled callers (e.g. the
    // header search, which navigates away after picking) keep the old empty-after-pick
    // behavior.
    setQuery(value !== undefined ? ticker : "");
    setOpen(false);
    if (onSelect) {
      onSelect(ticker, exchange);
    } else {
      navigate(
        `/analysis/stock-analysis/${ticker}${exchange ? `?exchange=${exchange}` : ""}`,
      );
    }
  };

  const handleResultSelect = (result: ListingSearchResult) => {
    onResultSelect?.(result);
    handleSelect(result.ticker, result.exchange);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }

    if (e.key === "Tab" && !trimmed && ghostTicker) {
      handleSelect(ghostTicker.ticker, ghostTicker.exchange);
      return;
    }

    if (!showDropdown || results.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (results[selectedIndex]) {
        handleResultSelect(results[selectedIndex]);
      }
    }
  };

  const inputHeight =
    size === "lg"
      ? `h-12 text-base ${showIcon ? "pl-12" : "pl-3"}`
      : `h-9 text-sm ${showIcon ? "pl-8" : "pl-3"}`;
  const iconSize =
    size === "lg" ? "h-5 w-5 left-3.5 top-3.5" : "h-4 w-4 left-2 top-2.5";

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      {/* Search icon */}
      {showIcon && (
        <Search
          className={cn(
            "absolute text-muted-foreground pointer-events-none z-10",
            iconSize,
          )}
        />
      )}

      <Input
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={
          ghostTicker && !trimmed
            ? ghostTicker.ticker
            : (placeholder ?? t("research:search.placeholder"))
        }
        maxLength={50}
        className={cn(inputHeight)}
      />

      {/* Autocomplete dropdown */}
      {showDropdown && (
        <div className="absolute z-50 mt-1 w-full min-w-[280px] overflow-hidden rounded-md border bg-popover shadow-lg">
          <div
            className="max-h-64 overflow-y-auto py-1"
            onMouseDown={(e) => e.preventDefault()} // prevent blur before click
          >
            {/* Loading spinner */}
            {isFetching && (
              <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t("research:search.searching")}
              </div>
            )}

            {/* Results */}
            {!isFetching &&
              results.length > 0 &&
              results.map((r, index) => (
                <button
                  key={r.id}
                  type="button"
                  className={cn(
                    "flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors",
                    index === selectedIndex
                      ? "bg-muted/80"
                      : "hover:bg-muted/80",
                  )}
                  onClick={() => handleResultSelect(r)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <CompanyLogo
                    ticker={r.ticker}
                    exchange={r.exchange}
                    companyName={r.company_name}
                    className="h-7 w-7 rounded-full bg-muted p-0.5 shrink-0"
                  />
                  <div className="flex flex-col min-w-0">
                    <span className="font-semibold text-foreground">
                      {r.ticker}
                    </span>
                    <span className="flex text-xs text-muted-foreground gap-1 min-w-0">
                      <span className="truncate">{r.company_name}</span>
                      {r.exchange && (
                        <span className="shrink-0">· {r.exchange}</span>
                      )}
                    </span>
                  </div>
                </button>
              ))}

            {/* Empty state for local results */}
            {!isFetching && results.length === 0 && trimmed.length >= 1 && (
              <div className="px-3 py-3 text-sm text-muted-foreground text-center">
                {t("research:search.noResults", { query: trimmed })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ResearchSearchInput;
